import { FormControl, FormHelperText, FormLabel, Box, IconButton } from '@mui/material';
import * as React from "react";
import Files from "react-files";
import Button from "../../components/Button/Button";
import {FileUploadWrapper} from "../../styles/jss/components/forms/form-file-upload";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import { LabelValuePair } from "../../model/LabelValuePair";
import UploadImagePreview from "../../views/UploadImagePreview";
import { useMemo, memo, useCallback } from 'react';

interface FileUploadProps {
    label?: string;
    description?: string;
    required?: boolean;
    error?: string;
    onChange: (files: any[]) => void;
    onError: (error: any, file: any) => void;
    visible?: boolean;
    options: {
        fileTypes: LabelValuePair[];
        fileUploadAreaText?: string;
        maxFiles?: number;
        maxFileSize?: number;
    };
    files: any[];
    onRemoveAll: () => void;
    onRemoveOne: (fileId: any) => void;
    onClearError: () => void;
    appValidated?: boolean;
}

const FileUpload = memo((props: FileUploadProps) => {
    const {
        label,
        description,
        required = false,
        error,
        onChange,
        onError,
        visible = true,
        options,
        files = [],
        onRemoveAll,
        onRemoveOne,
        onClearError,
        appValidated = false
    } = props;

    // Memoize computed values
    const showDescription = useMemo(() =>
            description !== undefined && description !== "",
        [description]
    );

    const showRequiredError = useMemo(() =>
            required && appValidated && files.length === 0,
        [required, appValidated, files.length]
    );

    const showOtherError = useMemo(() =>
            error !== undefined && error !== "" && !showRequiredError,
        [error, showRequiredError]
    );

    const showError = useMemo(() =>
            showRequiredError || showOtherError,
        [showRequiredError, showOtherError]
    );

    const errorMessage = useMemo(() =>
            showRequiredError ? 'This field is required' : error,
        [showRequiredError, error]
    );

    const fileTypeValues = useMemo(() =>
            options.fileTypes.map((fileType: LabelValuePair) => fileType.value),
        [options.fileTypes]
    );

    const fileTypeLabels = useMemo(() =>
            options.fileTypes.map((fileType: LabelValuePair) => fileType.label.toLowerCase()).join(", "),
        [options.fileTypes]
    );

    // Memoize event handlers
    const handleRemoveOne = useCallback((fileId: any) => {
        onRemoveOne(fileId);
    }, [onRemoveOne]);

    const handleRemoveAll = useCallback(() => {
        onRemoveAll();
    }, [onRemoveAll]);

    const handleClearError = useCallback(() => {
        onClearError();
    }, [onClearError]);

    const fileList = useMemo(() =>
            files.map((file: any) => (
                <li className="files-list-item" key={file.id}>
                    <div className="files-list-preview">
                        {file.preview?.type === "image" ? (
                            <UploadImagePreview file={file} />
                        ) : (
                            <div className="files-list-preview-card">
                                <div className="files-list-preview-extension">
                                    {String(file.extension ?? '').toUpperCase() || 'FILE'}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="files-list-content">
                        <div className="files-list-content-item files-list-content-name">{file.name}</div>
                        <div className="files-list-content-item files-list-content-size">{file.sizeReadable}</div>
                    </div>

                    <IconButton
                        id={file.id}
                        className="files-list-remove"
                        onClick={() => handleRemoveOne(file.id)}
                        aria-label="delete"
                    >
                        <ClearOutlinedIcon />
                    </IconButton>
                </li>
            )),
        [files, handleRemoveOne]
    );

    // Early return for hidden components
    if (!visible) {
        return null;
    }

    return (
        <Box sx={{ display: 'block' }}>
            <FileUploadWrapper>
                <FormControl className="form-control" fullWidth={true}>
                    <FormLabel required={required} color="secondary" className="form-separate-label">
                        {label}
                    </FormLabel>

                    <Files
                        className={`files-dropzone ${showError ? 'error' : ''}`}
                        onChange={onChange}
                        onError={onError}
                        accepts={fileTypeValues}
                        multiple
                        maxFiles={options.maxFiles}
                        maxFileSize={options.maxFileSize}
                        minFileSize={0}
                        clickable
                    >
                        <FileUploadIcon onDrop={handleClearError} onClick={handleClearError} />
                        <span onDrop={handleClearError} onClick={handleClearError} className="files-upload-area-text">
                            {options.fileUploadAreaText}
                        </span>
                                            <span onDrop={handleClearError} onClick={handleClearError} className="files-info-text">
                            Drop files here or click to upload
                        </span>
                                            <span onDrop={handleClearError} onClick={handleClearError} className="files-info-text files-info-text-small">
                            ({fileTypeLabels})
                        </span>
                    </Files>

                    {files.length > 0 && (
                        <div className="files-btn-hold">
                            <Button variant="text" size="small" onClick={handleRemoveAll}>
                                Remove All Files
                            </Button>
                        </div>
                    )}

                    {files.length > 0 && (
                        <div className="files-list-holder">
                            <ul className="files-list">
                                {fileList}
                            </ul>
                        </div>
                    )}

                    {showDescription && (
                        <FormHelperText>{description}</FormHelperText>
                    )}

                    {showError && (
                        <FormHelperText error={true}>
                            {errorMessage}
                        </FormHelperText>
                    )}
                </FormControl>
            </FileUploadWrapper>
        </Box>
    );
});

FileUpload.displayName = 'FileUpload';

export default FileUpload;
